import { MaterialCommunityIcons } from "@expo/vector-icons";
import {
    physicianSearchInputError,
    hasFullPhysicianName,
    isPhysicianPending,
    type PhysicianVerificationSource
} from "@taskhealth/shared";
import { useLocalSearchParams } from "expo-router";
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    ActivityIndicator,
    Animated,
    Platform,
    Vibration,
    FlatList,
    Keyboard,
    StyleSheet,
    TextInput,
    TouchableOpacity,
    View
} from "react-native";
import { Text } from "react-native-paper";

import {
    fetchPlaceDetails,
    fetchPredictions,
    hasPlacesApiKey,
    type PlaceDetails,
    type Prediction
} from "./places-autocomplete";
import type { QuestionComponentProps } from "./types";

import { useResolvedReducedMotion } from "@/hooks/useResolvedReducedMotion";
import { request as apiRequest } from "@/lib/api";
import { colors, fonts, radius, spacing } from "@/lib/theme";

interface SavedPhysicianLocation {
    address: string | null;
    phone: string | null;
    fax: string | null;
}

/** The top-level fields saved to visit_answers q115 are read by the CMS-485,
 *  Kardex and Visit Report loaders. Additive fields must keep those intact. */
interface SavedPhysician {
    fullName: string;
    address: string;
    phone: string | null;
    fax: string | null;
    npi: string | null;
    locations?: SavedPhysicianLocation[];
}

interface CardLocation {
    address: string | null;
    phone: string | null;
    fax?: string | null;
    distanceMiles?: number | null;
}

/** A card returned by the server-side physician resolver. The current resolver
 *  is local-table led, so NPI/phone/address are already attached to each card. */
interface SearchCard {
    kind: "knowledge" | "organic";
    name: string;
    type: string | null;
    address: string | null;
    phone: string | null;
    fax?: string | null;
    source: string | null;
    snippet: string | null;
    link: string | null;
    npi: string | null;
    confidence?: number;
    distanceMiles?: number | null;
    confidenceLabel?: "high";
    locations?: CardLocation[];
    match?: {
        nameScore?: number | null;
        addressScore?: number | null;
        distanceScore?: number | null;
    };
}

interface LookupResponse {
    lookupId?: number;
    best: SearchCard | null;
    candidates: SearchCard[];
    error?: string;
}

const cardKey = (c: SearchCard): string => c.npi ?? c.link ?? `${c.name}:${c.address ?? ""}`;

const isSearchCard = (card: SearchCard | null | undefined): card is SearchCard => Boolean(card);

const phoneDigitsOnly = (value: string): string => value.replace(/\D/g, "").slice(-10);

const formatPhoneInput = (value: string): string => {
    const digits = phoneDigitsOnly(value);
    if (digits.length === 0) {
        return "";
    }
    if (digits.length <= 3) {
        return `(${digits}`;
    }
    if (digits.length <= 6) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    }
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const isEmptyOrCompletePhone = (value: string): boolean => {
    const digits = phoneDigitsOnly(value);
    return digits.length === 0 || digits.length === 10;
};

const hasLocationData = (location: {
    address?: string | null;
    phone?: string | null;
    fax?: string | null;
}): boolean => Boolean(location.address || location.phone || location.fax);

const toSavedLocation = (location: {
    address?: string | null;
    phone?: string | null;
    fax?: string | null;
}): SavedPhysicianLocation => ({
    address: location.address ?? null,
    phone: location.phone ?? null,
    fax: location.fax ?? null
});

const locationsForCard = (card: SearchCard): SavedPhysicianLocation[] => {
    const rawLocations =
        card.locations && card.locations.length > 0
            ? card.locations
            : [{ address: card.address, phone: card.phone, fax: card.fax }];
    return rawLocations.map(toSavedLocation).filter(hasLocationData);
};

const locationsForSavedPhysician = (physician: SavedPhysician): SavedPhysicianLocation[] => {
    if (physician.locations && physician.locations.length > 0) {
        return physician.locations.filter(hasLocationData);
    }
    return [
        {
            address: physician.address || null,
            phone: physician.phone,
            fax: physician.fax
        }
    ].filter(hasLocationData);
};

const physicianFromCard = (card: SearchCard): SavedPhysician => {
    const locations = locationsForCard(card);
    return {
        fullName: card.name,
        address: card.address || "",
        phone: card.phone || null,
        fax: card.fax || null,
        npi: card.npi || null,
        ...(locations.length > 0 ? { locations } : {})
    };
};

const withAdditionalLocation = (
    physician: SavedPhysician,
    additionalAddress: string,
    additionalPhone: string,
    additionalFax: string
): SavedPhysician => {
    const manualLocation = {
        address: additionalAddress.trim() || null,
        phone: additionalPhone.trim() || null,
        fax: additionalFax.trim() || null
    };
    if (!hasLocationData(manualLocation)) {
        return physician;
    }
    const locations = [...locationsForSavedPhysician(physician), manualLocation];
    return {
        ...physician,
        locations
    };
};

export function PhysicianQuestion({ value, onChange, readOnly, onFocus }: QuestionComponentProps) {
    const { id: visitId } = useLocalSearchParams<{ id: string }>();

    // Previously saved answer (shown until she runs a new search).
    const saved = useMemo<SavedPhysician | null>(() => {
        if (value) {
            try {
                return JSON.parse(value) as SavedPhysician;
            } catch {
                return null;
            }
        }
        return null;
    }, [value]);

    const [name, setName] = useState("");
    const [address, setAddress] = useState("");
    const [addressPlace, setAddressPlace] = useState<PlaceDetails | null>(null);
    const [addressPredictions, setAddressPredictions] = useState<Prediction[]>([]);
    const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
    const [verificationCard, setVerificationCard] = useState<SearchCard | null>(null);
    const [verificationSource, setVerificationSource] =
        useState<PhysicianVerificationSource | null>(null);
    const [verificationDetails, setVerificationDetails] = useState("");
    const [requestingReview, setRequestingReview] = useState(false);
    const [searching, setSearching] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [invalidField, setInvalidField] = useState<"name" | "address" | null>(null);
    const nameInputRef = useRef<TextInput>(null);
    const addressInputRef = useRef<TextInput>(null);
    const errorOffset = useRef(new Animated.Value(0)).current;
    const errorAnimation = useRef<Animated.CompositeAnimation | null>(null);
    const motion = useResolvedReducedMotion();
    useEffect(() => {
        return () => {
            errorAnimation.current?.stop();
            errorOffset.setValue(0);
        };
    }, [errorOffset, motion.reduced]);

    const showInputError = (message: string, field: "name" | "address") => {
        setError(message);
        setInvalidField(field);
        (field === "name" ? nameInputRef : addressInputRef).current?.focus();
        errorAnimation.current?.stop();
        errorOffset.setValue(0);
        if (motion.resolved && !motion.reduced) {
            errorAnimation.current = Animated.sequence(
                [7, -7, 5, -5, 0].map((toValue) =>
                    Animated.timing(errorOffset, {
                        toValue,
                        duration: 60,
                        useNativeDriver: Platform.OS !== "web"
                    })
                )
            );
            errorAnimation.current.start();
        }
        if (Platform.OS !== "web") {
            Vibration.vibrate(100);
        }
    };
    const [results, setResults] = useState<LookupResponse | null>(null);
    const [selectedKey, setSelectedKey] = useState<string | null>(null);
    const [choosingKey, setChoosingKey] = useState<string | null>(null);
    const [selectedPhysician, setSelectedPhysician] = useState<SavedPhysician | null>(null);
    const [manualAddress, setManualAddress] = useState("");
    const [manualPhone, setManualPhone] = useState("");
    const [manualFax, setManualFax] = useState("");
    const [savingManualDetails, setSavingManualDetails] = useState(false);
    const [manualDetailsSaved, setManualDetailsSaved] = useState(false);
    const addressDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const displayedPhysician = selectedPhysician ?? saved;
    const canAddManualDetails =
        Boolean(selectedPhysician) && !isPhysicianPending(selectedPhysician);

    const authFetch = async (path: string, body: unknown) => {
        return apiRequest(path, {
            method: "POST",
            body: JSON.stringify(body)
        });
    };

    const handleSearch = async () => {
        const inputError = physicianSearchInputError(name, address);
        if (inputError) {
            showInputError(inputError, hasFullPhysicianName(name) ? "address" : "name");
            return;
        }
        setInvalidField(null);
        setVerificationCard(null);
        setVerificationSource(null);
        setVerificationDetails("");
        setSearching(true);
        setError(null);
        setResults(null);
        setSelectedKey(null);
        setChoosingKey(null);
        setSelectedPhysician(null);
        setManualAddress("");
        setManualPhone("");
        setManualFax("");
        setManualDetailsSaved(false);
        try {
            const data = (await authFetch(`/visits/${visitId}/physician-lookup`, {
                verificationFlowVersion: 1,
                name: name.trim(),
                address: address.trim(),
                addressLocation: addressPlace
                    ? { latitude: addressPlace.lat, longitude: addressPlace.lng }
                    : null
            })) as LookupResponse;
            if (data.error) {
                setError("Search unavailable. Please try again.");
                return;
            }
            setResults({
                ...data,
                best: isSearchCard(data.best) ? data.best : null,
                candidates: (data.candidates ?? []).filter(isSearchCard)
            });
        } catch (e) {
            setError(e instanceof Error ? e.message : "Failed to find physician");
        } finally {
            setSearching(false);
        }
    };

    const selectCard = async (c: SearchCard, verified = false) => {
        if (choosingKey || requestingReview || savingManualDetails || readOnly) {
            return;
        }
        if (results?.best?.npi !== c.npi && !verified) {
            setVerificationCard(c);
            setVerificationSource(null);
            setVerificationDetails("");
            return;
        }
        if (verified && (!verificationSource || !verificationDetails.trim())) {
            setError(
                "Choose who verified the physician and enter the identifying details you checked."
            );
            return;
        }
        const key = cardKey(c);
        const toSave = physicianFromCard(c);
        setChoosingKey(key);
        setError(null);
        try {
            const response = (await authFetch(`/visits/${visitId}/physician-confirm`, {
                verificationFlowVersion: 1,
                physician: toSave,
                lookupId: results?.lookupId,
                ...(verified
                    ? {
                          verification: {
                              source: verificationSource,
                              details: verificationDetails.trim()
                          }
                      }
                    : {})
            })) as { saved?: boolean; physician?: SavedPhysician };
            // During a rolling API release an older container returns only saved:true.
            const confirmedPhysician = response.physician ?? (response.saved ? toSave : null);
            if (!confirmedPhysician) {
                throw new Error("Could not confirm the saved physician. Please try again.");
            }
            setSelectedKey(key);
            setSelectedPhysician(confirmedPhysician);
            setVerificationCard(null);
            setResults(null);
            setManualAddress("");
            setManualPhone("");
            setManualFax("");
            setManualDetailsSaved(false);
            onChange(JSON.stringify(confirmedPhysician));
        } catch (e) {
            setSelectedKey(null);
            setError(e instanceof Error ? e.message : "Failed to save physician");
        } finally {
            setChoosingKey(null);
        }
    };

    const requestAgencyReview = async () => {
        if (choosingKey || requestingReview || savingManualDetails || readOnly) {
            return;
        }
        setRequestingReview(true);
        setError(null);
        try {
            const response = (await authFetch(`/visits/${visitId}/physician-verification`, {
                name: name.trim(),
                address: address.trim(),
                reason: "RN could not verify the physician with the patient, caregiver, or existing paperwork."
            })) as { physician: SavedPhysician };
            setSelectedPhysician(response.physician);
            setResults(null);
            setVerificationCard(null);
            onChange(JSON.stringify(response.physician));
        } catch (e) {
            setError(
                e instanceof Error
                    ? e.message
                    : "Could not request agency verification. Please try again."
            );
        } finally {
            setRequestingReview(false);
        }
    };

    const startNewSearch = () => {
        setVerificationCard(null);
        setResults(null);
        setSelectedKey(null);
        setChoosingKey(null);
        setSelectedPhysician(null);
        setManualAddress("");
        setManualPhone("");
        setManualFax("");
        setManualDetailsSaved(false);
        setError(null);
    };

    const saveManualDetails = async () => {
        if (
            !displayedPhysician ||
            (!manualAddress.trim() && !manualPhone.trim() && !manualFax.trim()) ||
            !isEmptyOrCompletePhone(manualPhone) ||
            !isEmptyOrCompletePhone(manualFax)
        ) {
            if (!isEmptyOrCompletePhone(manualPhone) || !isEmptyOrCompletePhone(manualFax)) {
                setError("Phone and fax must be 10 digits.");
            }
            return;
        }
        const updated = withAdditionalLocation(
            displayedPhysician,
            manualAddress,
            manualPhone,
            manualFax
        );
        setSavingManualDetails(true);
        setError(null);
        try {
            const response = (await authFetch(`/visits/${visitId}/physician-confirm`, {
                verificationFlowVersion: 1,
                physician: updated
            })) as { saved?: boolean; physician?: SavedPhysician };
            const confirmedPhysician = response.physician ?? (response.saved ? updated : null);
            if (!confirmedPhysician) {
                throw new Error("Could not confirm the saved details. Please try again.");
            }
            setSelectedPhysician(confirmedPhysician);
            setManualAddress("");
            setManualPhone("");
            setManualFax("");
            setManualDetailsSaved(true);
            onChange(JSON.stringify(confirmedPhysician));
        } catch {
            setError("Failed to save added details");
        } finally {
            setSavingManualDetails(false);
        }
    };

    const handleAddressChange = useCallback((text: string) => {
        setAddress(text);
        setInvalidField(null);
        setError(null);
        setResults(null);
        setVerificationCard(null);
        setAddressPlace((current) => {
            if (!current) {
                return null;
            }
            return current.address === text ? current : null;
        });

        if (addressDebounceRef.current) {
            clearTimeout(addressDebounceRef.current);
        }
        addressDebounceRef.current = setTimeout(async () => {
            if (!hasPlacesApiKey() || text.trim().length < 3) {
                setAddressPredictions([]);
                setShowAddressSuggestions(false);
                return;
            }
            const predictions = await fetchPredictions(text.trim());
            setAddressPredictions(predictions);
            setShowAddressSuggestions(predictions.length > 0);
        }, 300);
    }, []);

    const handleAddressSelect = useCallback(async (prediction: Prediction) => {
        setResults(null);
        setVerificationCard(null);
        setShowAddressSuggestions(false);
        setAddressPredictions([]);
        Keyboard.dismiss();

        const details = await fetchPlaceDetails(prediction.placeId);
        if (details) {
            setAddress(details.address);
            setAddressPlace(details);
        } else {
            setAddress(prediction.description);
            setAddressPlace(null);
        }
    }, []);

    return (
        <View style={styles.card}>
            <Text style={styles.inputLabel}>Physician first and last name (required)</Text>
            <TextInput
                ref={nameInputRef}
                style={[styles.input, invalidField === "name" && styles.invalidInput]}
                placeholder="e.g. Karen Johnson"
                placeholderTextColor={colors.textTertiary}
                value={name}
                onFocus={onFocus}
                onChangeText={(text) => {
                    setName(text);
                    setInvalidField(null);
                    setError(null);
                    setResults(null);
                    setVerificationCard(null);
                }}
                editable={!readOnly && !searching && !requestingReview && !choosingKey}
                autoCorrect={false}
                autoCapitalize="words"
                spellCheck={false}
            />

            {name.trim() && !hasFullPhysicianName(name) && (
                <Text style={styles.verificationHint}>
                    First and last name are required. If you cannot obtain both, ask the agency to
                    verify the physician.
                </Text>
            )}
            <Text style={styles.inputLabel}>Physician office address, city, or ZIP code</Text>
            <Text style={styles.verificationHint}>
                Enter any location details you know. Typed text is accepted; choosing a Google
                suggestion is optional.
            </Text>
            <View>
                <TextInput
                    ref={addressInputRef}
                    style={[styles.input, invalidField === "address" && styles.invalidInput]}
                    placeholder="e.g. 475 Seaview Ave, Brooklyn, or 11219"
                    placeholderTextColor={colors.textTertiary}
                    value={address}
                    onFocus={() => {
                        onFocus?.();
                        if (addressPredictions.length > 0) {
                            setShowAddressSuggestions(true);
                        }
                    }}
                    onBlur={() => {
                        setTimeout(() => setShowAddressSuggestions(false), 200);
                    }}
                    onChangeText={handleAddressChange}
                    editable={!readOnly && !searching && !requestingReview && !choosingKey}
                    autoCorrect={false}
                    autoCapitalize="words"
                    spellCheck={false}
                />
                {showAddressSuggestions && (
                    <View style={styles.suggestionsContainer}>
                        <FlatList
                            data={addressPredictions}
                            keyExtractor={(item) => item.placeId}
                            keyboardShouldPersistTaps="handled"
                            renderItem={({ item }) => (
                                <TouchableOpacity
                                    style={styles.suggestionItem}
                                    onPress={() => handleAddressSelect(item)}
                                >
                                    <Text style={styles.suggestionText}>{item.description}</Text>
                                </TouchableOpacity>
                            )}
                        />
                    </View>
                )}
                {addressPlace && (
                    <View style={styles.verifiedRow}>
                        <MaterialCommunityIcons
                            name="check-circle"
                            size={13}
                            color={colors.success500}
                        />
                        <Text style={styles.verifiedText}>Address selected</Text>
                    </View>
                )}
            </View>

            {error && (
                <Animated.View
                    accessibilityRole="alert"
                    accessibilityLiveRegion="assertive"
                    style={[styles.errorBanner, { transform: [{ translateX: errorOffset }] }]}
                >
                    <Text style={styles.errorTitle}>
                        {invalidField === "address"
                            ? "Add a physician location"
                            : invalidField === "name"
                              ? "Add the physician's full name"
                              : "Please check this information"}
                    </Text>
                    <Text style={styles.errorText}>{error}</Text>
                </Animated.View>
            )}

            {!readOnly && (
                <TouchableOpacity
                    onPress={handleSearch}
                    disabled={searching || requestingReview || Boolean(choosingKey)}
                    style={[styles.searchBtn, searching && styles.searchBtnDisabled]}
                >
                    {searching ? (
                        <>
                            <ActivityIndicator size="small" color="#fff" />
                            <Text style={styles.searchBtnText}>Searching…</Text>
                        </>
                    ) : (
                        <>
                            <MaterialCommunityIcons name="magnify" size={18} color="#fff" />
                            <Text style={styles.searchBtnText}>Search</Text>
                        </>
                    )}
                </TouchableOpacity>
            )}

            {!readOnly && !searching && (
                <TouchableOpacity
                    onPress={requestAgencyReview}
                    disabled={requestingReview || Boolean(choosingKey)}
                    style={styles.searchAgainBtn}
                >
                    <Text style={styles.searchAgainText}>
                        {requestingReview
                            ? "Saving review request…"
                            : "I cannot verify the physician — ask the agency"}
                    </Text>
                </TouchableOpacity>
            )}
            {verificationCard && !readOnly && (
                <View style={styles.resultCard}>
                    <Text style={styles.resultName}>Verify before saving</Text>
                    <Text style={styles.verificationHint}>
                        This physician was not designated a Best match. Confirm{" "}
                        {verificationCard.name}'s identifying details with the patient, caregiver,
                        or existing paperwork.
                    </Text>
                    <Text style={styles.inputLabel}>Who or what confirmed the physician?</Text>
                    {(
                        [
                            ["patient", "Patient"],
                            ["caregiver", "Caregiver"],
                            ["paperwork", "Existing paperwork"]
                        ] as const
                    ).map(([source, label]) => (
                        <TouchableOpacity
                            key={source}
                            onPress={() => setVerificationSource(source)}
                            style={styles.searchAgainBtn}
                        >
                            <Text style={styles.searchAgainText}>
                                {verificationSource === source ? "✓ " : ""}
                                {label}
                            </Text>
                        </TouchableOpacity>
                    ))}
                    <TextInput
                        style={styles.input}
                        value={verificationDetails}
                        onChangeText={setVerificationDetails}
                        multiline
                        placeholder="What did you verify? For example: full name and office address, practice name, or office phone."
                    />
                    <TouchableOpacity
                        style={styles.choosePhysicianBtn}
                        disabled={
                            !verificationSource ||
                            !verificationDetails.trim() ||
                            Boolean(choosingKey) ||
                            requestingReview
                        }
                        onPress={() => selectCard(verificationCard, true)}
                    >
                        <Text style={styles.choosePhysicianText}>
                            {choosingKey ? "Saving…" : "I verified these details — save physician"}
                        </Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                        style={styles.searchAgainBtn}
                        disabled={Boolean(choosingKey)}
                        onPress={() => setVerificationCard(null)}
                    >
                        <Text style={styles.searchAgainText}>Back to results</Text>
                    </TouchableOpacity>
                </View>
            )}

            {/* Saved answer, shown until she runs a new search */}
            {!results && displayedPhysician && (
                <View style={styles.results}>
                    <SavedCard physician={displayedPhysician} />
                    {!readOnly && canAddManualDetails && (
                        <ManualPhysicianDetails
                            address={manualAddress}
                            phone={manualPhone}
                            fax={manualFax}
                            saving={savingManualDetails}
                            saved={manualDetailsSaved}
                            onAddressChange={(text) => {
                                setManualAddress(text);
                                setManualDetailsSaved(false);
                            }}
                            onPhoneChange={(text) => {
                                setManualPhone(text);
                                setManualDetailsSaved(false);
                            }}
                            onFaxChange={(text) => {
                                setManualFax(text);
                                setManualDetailsSaved(false);
                            }}
                            onSave={saveManualDetails}
                        />
                    )}
                    {!readOnly && (
                        <TouchableOpacity onPress={startNewSearch} style={styles.searchAgainBtn}>
                            <Text style={styles.searchAgainText}>Search again</Text>
                        </TouchableOpacity>
                    )}
                </View>
            )}

            {/* Live lookup results */}
            {results && !verificationCard && (
                <View style={styles.results}>
                    {results.best && (
                        <ResultCard
                            card={results.best}
                            best
                            selected={selectedKey === cardKey(results.best)}
                            choosing={choosingKey === cardKey(results.best)}
                            onChoose={readOnly ? undefined : () => selectCard(results.best!)}
                        />
                    )}
                    {(results.candidates ?? []).length > 0 && (
                        <Text style={styles.chooseLabel}>Choose the correct physician</Text>
                    )}
                    {(results.candidates ?? []).map((c) => (
                        <ResultCard
                            key={cardKey(c)}
                            card={c}
                            selected={selectedKey === cardKey(c)}
                            choosing={choosingKey === cardKey(c)}
                            onChoose={readOnly ? undefined : () => selectCard(c)}
                        />
                    ))}
                    {!results.best && (results.candidates ?? []).length === 0 && (
                        <Text style={styles.emptyText}>
                            We couldn't find anyone with this info. Try again with a full name or
                            address.
                        </Text>
                    )}
                </View>
            )}
        </View>
    );
}

const percentScore = (score: number | null | undefined): string | null => {
    if (score == null || Number.isNaN(score)) {
        return null;
    }
    return `${Math.round(score * 100)}%`;
};

function ResultCard({
    card,
    best,
    selected,
    choosing,
    onChoose
}: {
    card: SearchCard;
    best?: boolean;
    selected: boolean;
    choosing?: boolean;
    onChoose?: () => void;
}) {
    const specialty = card.type ?? "";
    const locations = locationsForCard(card);
    const scoreChips = [
        { label: "Name", value: percentScore(card.match?.nameScore) },
        { label: "Physician address", value: percentScore(card.match?.addressScore) },
        { label: "Patient distance", value: percentScore(card.match?.distanceScore) }
    ].filter((chip): chip is { label: string; value: string } => Boolean(chip.value));
    return (
        <View
            style={[
                styles.resultCard,
                best && styles.resultCardBest,
                selected && styles.resultCardSelected
            ]}
        >
            {best && <Text style={styles.bestPill}>Best match</Text>}
            <Text style={styles.resultName}>{card.name}</Text>
            {!!specialty && <Text style={styles.resultSpecialty}>{specialty}</Text>}
            <Text style={[styles.confidenceBadge, styles.highConfidenceBadge]}>
                {card.confidence == null
                    ? "Confidence unavailable"
                    : `${card.confidence}% confidence`}
            </Text>
            {scoreChips.length > 0 && (
                <View style={styles.scoreChipRow}>
                    {scoreChips.map((chip) => (
                        <Text key={chip.label} style={styles.scoreChip}>
                            {chip.label} {chip.value}
                        </Text>
                    ))}
                </View>
            )}
            {locations.map((location, index) => (
                <View
                    key={`${location.address || ""}:${location.phone || ""}:${location.fax || ""}:${index}`}
                    style={styles.locationGroup}
                >
                    {!!location.address && (
                        <ResultLine icon="map-marker">{location.address}</ResultLine>
                    )}
                    {!!location.phone && <ResultLine icon="phone">{location.phone}</ResultLine>}
                    {!!location.fax && <ResultLine icon="fax">Fax {location.fax}</ResultLine>}
                </View>
            ))}
            {locations.length === 0 && (
                <ResultLine icon="map-marker">No address returned</ResultLine>
            )}
            {!!onChoose && !selected && (
                <TouchableOpacity
                    onPress={onChoose}
                    disabled={choosing}
                    style={[styles.choosePhysicianBtn, choosing && styles.choosePhysicianBtnBusy]}
                >
                    {choosing ? (
                        <ActivityIndicator size="small" color="#fff" />
                    ) : (
                        <MaterialCommunityIcons name="check-circle" size={16} color="#fff" />
                    )}
                    <Text style={styles.choosePhysicianText}>
                        {choosing ? "Saving..." : "Choose this physician"}
                    </Text>
                </TouchableOpacity>
            )}
        </View>
    );
}

function SavedCard({ physician }: { physician: SavedPhysician }) {
    const locations = locationsForSavedPhysician(physician);
    return (
        <View style={[styles.resultCard, styles.resultCardSelected]}>
            <Text style={styles.resultName}>{physician.fullName}</Text>
            {isPhysicianPending(physician) && (
                <Text style={styles.emptyText}>
                    Saved for agency review. You can continue and finish the visit.
                </Text>
            )}
            {locations.map((location, index) => (
                <View
                    key={`${location.address || ""}:${location.phone || ""}:${location.fax || ""}:${index}`}
                    style={styles.locationGroup}
                >
                    {!!location.address && (
                        <ResultLine icon="map-marker">{location.address}</ResultLine>
                    )}
                    {!!location.phone && <ResultLine icon="phone">{location.phone}</ResultLine>}
                    {!!location.fax && <ResultLine icon="fax">Fax {location.fax}</ResultLine>}
                </View>
            ))}
        </View>
    );
}

function ManualPhysicianDetails({
    address,
    phone,
    fax,
    saving,
    saved,
    onAddressChange,
    onPhoneChange,
    onFaxChange,
    onSave
}: {
    address: string;
    phone: string;
    fax: string;
    saving: boolean;
    saved: boolean;
    onAddressChange: (text: string) => void;
    onPhoneChange: (text: string) => void;
    onFaxChange: (text: string) => void;
    onSave: () => void;
}) {
    const [addressPlace, setAddressPlace] = useState<PlaceDetails | null>(null);
    const [addressPredictions, setAddressPredictions] = useState<Prediction[]>([]);
    const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
    const addressDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const canSave = Boolean(address.trim() || phone.trim() || fax.trim());

    useEffect(() => {
        return () => {
            if (addressDebounceRef.current) {
                clearTimeout(addressDebounceRef.current);
            }
        };
    }, []);

    const handleAddressChange = useCallback(
        (text: string) => {
            onAddressChange(text);
            setAddressPlace((current) => {
                if (!current) {
                    return null;
                }
                return current.address === text || current.address.startsWith(text)
                    ? current
                    : null;
            });

            if (addressDebounceRef.current) {
                clearTimeout(addressDebounceRef.current);
            }
            addressDebounceRef.current = setTimeout(async () => {
                if (!hasPlacesApiKey() || text.trim().length < 3) {
                    setAddressPredictions([]);
                    setShowAddressSuggestions(false);
                    return;
                }
                const predictions = await fetchPredictions(text.trim());
                setAddressPredictions(predictions);
                setShowAddressSuggestions(predictions.length > 0);
            }, 300);
        },
        [onAddressChange]
    );

    const handleAddressSelect = useCallback(
        async (prediction: Prediction) => {
            setShowAddressSuggestions(false);
            setAddressPredictions([]);
            Keyboard.dismiss();

            const details = await fetchPlaceDetails(prediction.placeId);
            if (details) {
                onAddressChange(details.address);
                setAddressPlace(details);
            } else {
                onAddressChange(prediction.description);
                setAddressPlace(null);
            }
        },
        [onAddressChange]
    );

    useEffect(() => {
        if (!address.trim()) {
            setAddressPlace(null);
            setAddressPredictions([]);
            setShowAddressSuggestions(false);
        }
    }, [address]);

    const phoneDigits = phoneDigitsOnly(phone);
    const faxDigits = phoneDigitsOnly(fax);
    const phoneError = phoneDigits.length > 0 && phoneDigits.length < 10;
    const faxError = faxDigits.length > 0 && faxDigits.length < 10;
    const phoneSaveBlocked = phoneError || faxError;

    return (
        <View style={styles.manualDetails}>
            <Text style={styles.manualDetailsTitle}>Optional patient-provided details</Text>
            <Text style={styles.manualDetailsHelp}>
                If this is the patient's physician but an address, phone, or fax is missing, add
                what the patient gave you here.
            </Text>

            <Text style={styles.manualInputLabel}>Additional address (optional)</Text>
            <View>
                <TextInput
                    style={styles.manualInput}
                    placeholder="Address told by the patient"
                    placeholderTextColor={colors.textTertiary}
                    value={address}
                    onFocus={() => {
                        if (addressPredictions.length > 0) {
                            setShowAddressSuggestions(true);
                        }
                    }}
                    onBlur={() => {
                        setTimeout(() => setShowAddressSuggestions(false), 200);
                    }}
                    onChangeText={handleAddressChange}
                    editable={!saving}
                    autoCorrect={false}
                    autoCapitalize="words"
                    spellCheck={false}
                />
                {showAddressSuggestions && (
                    <View style={styles.suggestionsContainer}>
                        <FlatList
                            data={addressPredictions}
                            keyExtractor={(item) => item.placeId}
                            keyboardShouldPersistTaps="handled"
                            renderItem={({ item }) => (
                                <TouchableOpacity
                                    style={styles.suggestionItem}
                                    onPress={() => handleAddressSelect(item)}
                                >
                                    <Text style={styles.suggestionText}>{item.description}</Text>
                                </TouchableOpacity>
                            )}
                        />
                    </View>
                )}
                {addressPlace && (
                    <View style={styles.verifiedRow}>
                        <MaterialCommunityIcons
                            name="check-circle"
                            size={13}
                            color={colors.success500}
                        />
                        <Text style={styles.verifiedText}>Address selected</Text>
                    </View>
                )}
            </View>

            <Text style={styles.manualInputLabel}>Additional phone (optional)</Text>
            <TextInput
                style={[styles.manualInput, phoneError && styles.manualInputError]}
                placeholder="Phone told by the patient"
                placeholderTextColor={colors.textTertiary}
                value={phone}
                onChangeText={(text) => onPhoneChange(formatPhoneInput(text))}
                editable={!saving}
                keyboardType="phone-pad"
                inputMode="tel"
                maxLength={14}
            />
            {phoneError && (
                <Text style={styles.manualInputErrorText}>
                    Phone must be 10 digits ({phoneDigits.length} of 10).
                </Text>
            )}

            <Text style={styles.manualInputLabel}>Additional fax (optional)</Text>
            <TextInput
                style={[styles.manualInput, faxError && styles.manualInputError]}
                placeholder="Fax told by the patient"
                placeholderTextColor={colors.textTertiary}
                value={fax}
                onChangeText={(text) => onFaxChange(formatPhoneInput(text))}
                editable={!saving}
                keyboardType="phone-pad"
                inputMode="tel"
                maxLength={14}
            />
            {faxError && (
                <Text style={styles.manualInputErrorText}>
                    Fax must be 10 digits ({faxDigits.length} of 10).
                </Text>
            )}

            <TouchableOpacity
                onPress={onSave}
                disabled={!canSave || saving || phoneSaveBlocked}
                style={[
                    styles.saveManualBtn,
                    (!canSave || saving || phoneSaveBlocked) && styles.saveManualBtnDisabled
                ]}
            >
                {saving ? (
                    <ActivityIndicator size="small" color="#fff" />
                ) : (
                    <MaterialCommunityIcons name="content-save" size={15} color="#fff" />
                )}
                <Text style={styles.saveManualText}>
                    {saving ? "Saving..." : "Save added details"}
                </Text>
            </TouchableOpacity>
            {saved && <Text style={styles.manualSavedText}>Added to this physician.</Text>}
        </View>
    );
}

function ResultLine({ icon, children }: { icon: string; children: ReactNode }) {
    return (
        <View style={styles.resultLine}>
            <MaterialCommunityIcons
                name={icon as keyof typeof MaterialCommunityIcons.glyphMap}
                size={14}
                color={colors.textTertiary}
                style={styles.resultLineIcon}
            />
            <Text style={styles.resultLineText}>{children}</Text>
        </View>
    );
}

const styles = StyleSheet.create({
    card: {
        marginTop: spacing.sm
    },
    inputLabel: {
        fontSize: 13,
        fontFamily: fonts.medium,
        color: colors.textSecondary,
        marginBottom: 4,
        marginTop: spacing.sm
    },
    input: {
        borderWidth: 1.5,
        borderColor: colors.border,
        borderRadius: radius.md,
        padding: spacing.sm,
        fontSize: 15,
        fontFamily: fonts.regular,
        color: colors.textPrimary,
        backgroundColor: colors.surface
    },
    suggestionsContainer: {
        borderWidth: 1.5,
        borderTopWidth: 0,
        borderColor: colors.border,
        borderBottomLeftRadius: radius.md,
        borderBottomRightRadius: radius.md,
        backgroundColor: colors.surface,
        maxHeight: 190,
        overflow: "hidden"
    },
    suggestionItem: {
        paddingHorizontal: spacing.sm,
        paddingVertical: 10,
        borderBottomWidth: StyleSheet.hairlineWidth,
        borderBottomColor: colors.border
    },
    suggestionText: {
        fontSize: 13,
        fontFamily: fonts.regular,
        color: colors.textPrimary
    },
    verifiedRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 4,
        marginTop: 5
    },
    verifiedText: {
        fontSize: 11.5,
        fontFamily: fonts.medium,
        color: colors.success500
    },
    searchBtn: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: spacing.sm,
        backgroundColor: colors.teal500,
        borderRadius: radius.md,
        paddingVertical: 12,
        marginTop: spacing.base
    },
    searchBtnDisabled: {
        opacity: 0.7
    },
    searchBtnText: {
        fontSize: 15,
        fontFamily: fonts.semiBold,
        color: "#fff"
    },
    invalidInput: {
        borderWidth: 2,
        borderColor: colors.error600
    },
    errorBanner: {
        backgroundColor: colors.error50,
        borderWidth: 1,
        borderColor: colors.error600,
        borderRadius: radius.md,
        padding: spacing.md,
        marginTop: spacing.sm
    },
    errorTitle: {
        fontSize: 15,
        fontFamily: fonts.bold,
        color: colors.error600,
        marginBottom: spacing.xs
    },
    errorText: {
        fontSize: 14,
        fontFamily: fonts.regular,
        lineHeight: 21,
        color: colors.error600
    },
    results: {
        marginTop: spacing.base
    },
    chooseLabel: {
        fontSize: 13,
        fontFamily: fonts.medium,
        color: colors.textSecondary,
        marginBottom: spacing.sm,
        marginTop: spacing.xs
    },
    verificationHint: {
        fontSize: 14,
        fontFamily: fonts.regular,
        color: colors.textSecondary,
        lineHeight: 21,
        marginVertical: spacing.sm
    },
    emptyText: {
        fontSize: 13,
        fontFamily: fonts.regular,
        color: colors.textTertiary,
        textAlign: "center",
        paddingVertical: spacing.base
    },
    resultCard: {
        borderWidth: 1.5,
        borderColor: colors.border,
        borderRadius: radius.md,
        padding: spacing.md,
        marginBottom: spacing.sm,
        backgroundColor: colors.surface
    },
    resultCardBest: {
        borderColor: colors.teal400
    },
    resultCardSelected: {
        borderColor: colors.teal400,
        backgroundColor: colors.teal50
    },
    bestPill: {
        alignSelf: "flex-start",
        fontSize: 10,
        fontFamily: fonts.bold,
        letterSpacing: 0.3,
        textTransform: "uppercase",
        color: "#fff",
        backgroundColor: colors.teal500,
        borderRadius: 9999,
        paddingHorizontal: 8,
        paddingVertical: 2,
        marginBottom: 6,
        overflow: "hidden"
    },
    resultName: {
        fontSize: 15,
        fontFamily: fonts.semiBold,
        color: colors.textPrimary
    },
    resultSpecialty: {
        fontSize: 12.5,
        fontFamily: fonts.regular,
        color: colors.textSecondary,
        marginTop: 1
    },
    confidenceBadge: {
        alignSelf: "flex-start",
        fontSize: 11,
        fontFamily: fonts.semiBold,
        borderRadius: 7,
        paddingHorizontal: 7,
        paddingVertical: 2,
        marginTop: 8,
        overflow: "hidden"
    },
    highConfidenceBadge: {
        color: colors.success600,
        backgroundColor: colors.success50
    },
    scoreChipRow: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 6,
        marginTop: 7
    },
    scoreChip: {
        fontSize: 10.5,
        fontFamily: fonts.medium,
        color: colors.textSecondary,
        backgroundColor: colors.background,
        borderColor: colors.border,
        borderWidth: 1,
        borderRadius: 999,
        paddingHorizontal: 8,
        paddingVertical: 3,
        overflow: "hidden"
    },
    resultLine: {
        flexDirection: "row",
        alignItems: "flex-start",
        gap: spacing.sm,
        marginTop: 6
    },
    resultLineIcon: {
        marginTop: 1
    },
    resultLineText: {
        flex: 1,
        fontSize: 12.5,
        fontFamily: fonts.regular,
        color: colors.textSecondary
    },
    locationGroup: {
        marginTop: 7
    },
    choosePhysicianBtn: {
        alignSelf: "flex-start",
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        backgroundColor: colors.teal500,
        borderRadius: 999,
        paddingHorizontal: 12,
        paddingVertical: 8,
        marginTop: 12
    },
    choosePhysicianBtnBusy: {
        opacity: 0.72
    },
    choosePhysicianText: {
        fontSize: 12.5,
        fontFamily: fonts.semiBold,
        color: "#fff"
    },
    manualDetails: {
        borderTopWidth: 1,
        borderTopColor: colors.border,
        paddingTop: spacing.md,
        marginTop: spacing.xs,
        marginBottom: spacing.sm
    },
    manualDetailsTitle: {
        fontSize: 13,
        fontFamily: fonts.semiBold,
        color: colors.textPrimary
    },
    manualDetailsHelp: {
        fontSize: 12,
        fontFamily: fonts.regular,
        color: colors.textSecondary,
        lineHeight: 17,
        marginTop: 4,
        marginBottom: spacing.sm
    },
    manualInputLabel: {
        fontSize: 12,
        fontFamily: fonts.medium,
        color: colors.textSecondary,
        marginTop: spacing.xs,
        marginBottom: 4
    },
    manualInput: {
        borderWidth: 1.5,
        borderColor: colors.border,
        borderRadius: radius.md,
        paddingHorizontal: spacing.sm,
        paddingVertical: 9,
        fontSize: 14,
        fontFamily: fonts.regular,
        color: colors.textPrimary,
        backgroundColor: colors.surface
    },
    manualInputError: {
        borderColor: colors.error500
    },
    manualInputErrorText: {
        fontSize: 11.5,
        fontFamily: fonts.medium,
        color: colors.error500,
        marginTop: 4
    },
    saveManualBtn: {
        alignSelf: "flex-start",
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        backgroundColor: colors.teal500,
        borderRadius: radius.sm,
        paddingHorizontal: 10,
        paddingVertical: 8,
        marginTop: spacing.sm
    },
    saveManualBtnDisabled: {
        opacity: 0.45
    },
    saveManualText: {
        fontSize: 12.5,
        fontFamily: fonts.semiBold,
        color: "#fff"
    },
    manualSavedText: {
        fontSize: 12,
        fontFamily: fonts.medium,
        color: colors.success600,
        marginTop: 6
    },
    flag: {
        fontSize: 10.5,
        fontFamily: fonts.semiBold,
        color: colors.warning500,
        backgroundColor: colors.warning50,
        borderRadius: 6,
        paddingHorizontal: 6,
        paddingVertical: 1,
        overflow: "hidden"
    },
    searchAgainBtn: {
        alignItems: "center",
        marginTop: spacing.sm
    },
    searchAgainText: {
        fontSize: 13,
        fontFamily: fonts.medium,
        color: colors.teal400
    }
});
